/**
 * Pure support helpers for PtySession — node-pty process loading, prompt-byte
 * encoding, the per-turn options projection, and the AskUserQuestion parser.
 * Extracted from `session.ts` so the orchestrator stays focused on lifecycle;
 * everything here is side-effect-free (or a thin wrapper over one) and unit
 * tested in isolation.
 */

import { stat } from "node:fs/promises";
import type { HarnessQueryOptions, HarnessUserQuestion } from "../types.js";

// Cap on the rolling tail of raw terminal output retained for diagnostics when
// `claude` exits without a result. Bounded so a long-running session can't grow
// this unboundedly; only the most recent bytes (where a failure surfaces) matter.
export const MAX_DIAGNOSTIC_OUTPUT = 4000;

// Cap on transcript events buffered while no turn is draining (the parked
// window between a completed turn and the next beginTurn / a passive turn).
// Bounded, drop-oldest — a stale buffered `result` must never terminate a
// fresh turn, and beginTurn clears the buffer anyway.
export const MAX_BETWEEN_TURN_BUFFER = 500;

/** Settle window between the paste write and the submitting Enter write. */
export const SUBMIT_SETTLE_MS = 300;
// Re-press cadence/bounds for the submit nudge (see armSubmitNudge). Fast
// presses cover the Enter-swallowed-at-startup race; the slow phase covers
// startup dialogs that render tens of seconds in on a cold pod (folder
// trust, onboarding) — observed live 2026-07-02 parking code reviews ~13min+
// when the trust dialog mounted after the fast window had expired.
export const SUBMIT_NUDGE_INTERVAL_MS = 2000;
export const SUBMIT_NUDGE_MAX_PRESSES = 5;
export const SUBMIT_NUDGE_SLOW_INTERVAL_MS = 5000;
export const SUBMIT_NUDGE_WINDOW_MS = 90_000;

// Plan-dialog auto-accept press cadence/bounds (see armPlanDialogAutoAccept).
// The dialog usually renders within a second of the ExitPlanMode hook verdict,
// but the window is deliberately wide: the only stop signal is the ExitPlanMode
// PostToolUse envelope, and a missed dialog parks an auto card indefinitely
// (observed live 2026-07-07: an approval dialog sat 42 minutes until a human
// pressed Enter).
export const PLAN_DIALOG_FIRST_PRESS_MS = 700;
export const PLAN_DIALOG_INTERVAL_MS = 1500;
export const PLAN_DIALOG_SLOW_INTERVAL_MS = 5000;
export const PLAN_DIALOG_FAST_WINDOW_MS = 10_000;
export const PLAN_DIALOG_WINDOW_MS = 90_000;

function envMs(name: string, fallback: number): number {
  const raw = Number(process.env[name]);
  return Number.isFinite(raw) && raw > 0 ? raw : fallback;
}

export function resolveSubmitSettleMs(): number {
  return envMs("CONVEYOR_PTY_SUBMIT_SETTLE_MS", SUBMIT_SETTLE_MS);
}

/**
 * Effective submit-nudge timing. The exported constants above are the production
 * defaults; `CONVEYOR_PTY_NUDGE_INTERVAL_MS` / `_SLOW_INTERVAL_MS` / `_WINDOW_MS`
 * override them. Resolved at nudge-arm time (not import) so an integration test
 * can drive the two-phase fast→slow behavior in milliseconds instead of ~15s of
 * real wall-clock. `maxPresses` is deliberately NOT overridable — the fast→slow
 * phase-boundary count is the behavior under test.
 */
export function resolveSubmitNudgeTiming(): {
  intervalMs: number;
  slowIntervalMs: number;
  maxPresses: number;
  windowMs: number;
} {
  return {
    intervalMs: envMs("CONVEYOR_PTY_NUDGE_INTERVAL_MS", SUBMIT_NUDGE_INTERVAL_MS),
    slowIntervalMs: envMs("CONVEYOR_PTY_NUDGE_SLOW_INTERVAL_MS", SUBMIT_NUDGE_SLOW_INTERVAL_MS),
    maxPresses: SUBMIT_NUDGE_MAX_PRESSES,
    windowMs: envMs("CONVEYOR_PTY_NUDGE_WINDOW_MS", SUBMIT_NUDGE_WINDOW_MS),
  };
}

export function resolvePlanDialogTiming(): {
  firstPressMs: number;
  intervalMs: number;
  slowIntervalMs: number;
  fastWindowMs: number;
  windowMs: number;
} {
  return {
    firstPressMs: envMs("CONVEYOR_PTY_PLAN_DIALOG_FIRST_PRESS_MS", PLAN_DIALOG_FIRST_PRESS_MS),
    intervalMs: envMs("CONVEYOR_PTY_PLAN_DIALOG_INTERVAL_MS", PLAN_DIALOG_INTERVAL_MS),
    slowIntervalMs: envMs("CONVEYOR_PTY_PLAN_DIALOG_SLOW_INTERVAL_MS", PLAN_DIALOG_SLOW_INTERVAL_MS),
    fastWindowMs: envMs("CONVEYOR_PTY_PLAN_DIALOG_FAST_WINDOW_MS", PLAN_DIALOG_FAST_WINDOW_MS),
    windowMs: envMs("CONVEYOR_PTY_PLAN_DIALOG_WINDOW_MS", PLAN_DIALOG_WINDOW_MS),
  };
}

/**
 * The per-turn subset of HarnessQueryOptions — everything that legitimately
 * changes from one turn to the next while the same `claude` process serves them
 * all. The process-level fields (model, cwd, sessionId, appendSystemPrompt, …)
 * are fixed at spawn and captured separately in the spawn options.
 */
export interface TurnOptions {
  canUseTool?: HarnessQueryOptions["canUseTool"];
  promptDelivery?: "submit" | "prefill";
  planDialogAutoAccept?: boolean;
  abortController?: AbortController;
}

export function turnOptionsFrom(options: HarnessQueryOptions): TurnOptions {
  return {
    canUseTool: options.canUseTool,
    promptDelivery: options.promptDelivery,
    planDialogAutoAccept: options.planDialogAutoAccept,
    abortController: options.abortController,
  };
}

export interface PtyProcess {
  onData(listener: (data: string) => void): void;
  onExit(listener: (event: { exitCode: number }) => void): void;
  write(data: string): void;
  resize(cols: number, rows: number): void;
  kill(signal?: string): void;
}

export interface PtySpawnOptions {
  name: string;
  cols: number;
  rows: number;
  cwd: string;
  env: Record<string, string>;
}

export type PtySpawn = (file: string, args: string[], options: PtySpawnOptions) => PtyProcess;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function extractSpawn(mod: unknown): PtySpawn | null {
  if (!isRecord(mod)) return null;
  if (typeof mod.spawn === "function") return mod.spawn as PtySpawn;
  const def = mod.default;
  if (isRecord(def) && typeof def.spawn === "function") return def.spawn as PtySpawn;
  return null;
}

export async function loadPtySpawn(): Promise<PtySpawn> {
  const mod: unknown = await import("node-pty");
  const spawn = extractSpawn(mod);
  if (!spawn) throw new Error("node-pty: spawn export not found");
  return spawn;
}

export function inheritedEnv(socketPath?: string): Record<string, string> {
  const env: Record<string, string> = {};
  for (const [key, value] of Object.entries(process.env)) {
    if (typeof value === "string") env[key] = value;
  }
  // Do NOT force CLAUDE_CONFIG_DIR. Forcing it to ~/.claude makes the CLI read
  // <dir>/.claude.json — a fresh, un-onboarded config — instead of the user's
  // real ~/.claude.json, which re-triggers the first-run theme picker + login
  // even though valid credentials already exist in ~/.claude/. The loop above
  // already propagates an explicitly-set CLAUDE_CONFIG_DIR (pods/codespaces),
  // and the config dir is unchanged either way, so transcript tailing (which
  // uses claudeConfigHome()) still resolves to the same projects/ directory.
  if (socketPath) {
    env.CONVEYOR_HOOK_SOCKET = socketPath;
  }
  // The conveyor MCP tool handlers proxy over the agent's API socket, and a
  // mid-flap call can block up to ~50s (20s reconnect-wait + 30s ack) before
  // failing with a retryable error. The CLI's default MCP timeouts are
  // shorter — hitting them made it abandon the MCP session mid-flap
  // (2026-07-08 wedge). Default both timeouts past the worst-case hang;
  // operator-set values win via the inherit loop above.
  env.MCP_TIMEOUT ??= "60000";
  env.MCP_TOOL_TIMEOUT ??= "180000";
  return env;
}

/**
 * The bracketed-paste bytes written to the CLI's stdin to deliver a prompt.
 * The text is wrapped in paste markers so embedded newlines land as literal
 * lines in the input box instead of submitting it early. The submitting Enter
 * is deliberately NOT part of these bytes: deliverPrompt() sends it as a
 * separate write after a settle window, because an Enter folded into the same
 * chunk as the paste can be swallowed by the CLI's paste-burst handling —
 * observed in production as an auto-mode prompt parked unsubmitted in the
 * input box. "prefill" delivery never sends an Enter at all.
 */
export function buildPromptBytes(text: string): string {
  return `\x1b[200~${text}\x1b[201~`;
}

/**
 * Render structured (multimodal) prompt content as pasteable text. Image
 * blocks are replaced with a tool reference — their base64 payload must NEVER
 * be pasted into the TUI input box. Upstream prompt builders already skip
 * image blocks for the PTY harness; this is the last line of defense.
 */
export function renderPromptContentText(content: unknown[]): string {
  return content
    .map((block) => {
      const b = block as { type?: string; text?: string };
      if (b?.type === "text" && typeof b.text === "string") return b.text;
      if (b?.type === "image") {
        return `[Image attachment — use list_task_files / get_attachment to view]`;
      }
      return JSON.stringify(block);
    })
    .join("\n\n");
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Current size of the transcript, or 0 if it does not exist yet. */
export async function transcriptSize(path: string): Promise<number> {
  try {
    return (await stat(path)).size;
  } catch {
    return 0;
  }
}

/**
 * Defensive extraction of AskUserQuestion's `questions` input from the hook
 * payload. Malformed entries are dropped rather than thrown — the event is
 * observe-only, so an empty list still arms the waiting_for_input report.
 */
export function parseUserQuestions(input: Record<string, unknown>): HarnessUserQuestion[] {
  if (!Array.isArray(input.questions)) return [];
  const questions: HarnessUserQuestion[] = [];
  for (const entry of input.questions) {
    if (!isRecord(entry)) continue;
    if (typeof entry.question !== "string") continue;
    const options = Array.isArray(entry.options)
      ? entry.options
          .filter(isRecord)
          .filter((o) => typeof o.label === "string")
          .map((o) => ({
            label: o.label as string,
            description: typeof o.description === "string" ? o.description : "",
          }))
      : [];
    questions.push({
      question: entry.question,
      header: typeof entry.header === "string" ? entry.header : "",
      options,
      ...(typeof entry.multiSelect === "boolean" ? { multiSelect: entry.multiSelect } : {}),
    });
  }
  return questions;
}
